import React, { useState } from 'react';
import {
  IAvailableWorkflowActions,
  ISubmissionMetadata
} from '@publishing-engine/domain/models';
import { AIToolsEmptyState } from './AIToolsEmptyState';
import { Flex } from '@chakra-ui/react';
import { FieldHeader } from '../common/FieldHeader';
import { FormFieldAITools } from '@publishing-engine/domain/types/article';
import { EntryPoint } from 'src/utils/types';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import { AIToolsDiffModal } from './AIToolsDiffModal';
import { AIToolsDisplay } from './AIToolsDisplay';
import AIToolsUpdateModal from './AIToolsUpdateModal';

type IProps = {
  formFieldDefinition: FormFieldAITools;
  value?: ISubmissionMetadata['aiTools'];
  previousValue?: ISubmissionMetadata['aiTools'];
  isEditable?: boolean;
  availableActions: IAvailableWorkflowActions;
  onChange: (value: ISubmissionMetadata['aiTools']) => void;
  entryPoint: EntryPoint;
  isUpdateRequired?: boolean;
};

export const AITools: React.FC<IProps> = ({
  formFieldDefinition,
  value,
  previousValue,
  isEditable,
  availableActions,
  onChange,
  entryPoint,
  isUpdateRequired
}) => {
  const { checkFormFieldValueIsUpdated } = useSubmissionFields(entryPoint);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const isUpdated = checkFormFieldValueIsUpdated(formFieldDefinition.name);

  const hasValue = value?.exists === false || value?.exists === true;

  const editCallback = isEditable
    ? () => {
        setIsEditing(true);
      }
    : undefined;
  const [showDiffModal, setShowDiffModal] = useState<boolean>(false);
  const openDiffModal = () => setShowDiffModal(true);
  const closeDiffModal = () => setShowDiffModal(false);

  return (
    <Flex flexDirection={'column'} gap={2}>
      {isEditing && (
        <AIToolsUpdateModal
          formField={formFieldDefinition}
          isOpen={isEditing}
          value={value}
          onClose={() => {
            setIsEditing(false);
            onchange;
          }}
          onSubmit={value => {
            setIsEditing(false);
            onChange(value.metadata.aiTools);
          }}
        />
      )}

      {isUpdated && showDiffModal ? (
        <AIToolsDiffModal
          exists={value?.exists}
          previousExists={previousValue?.exists}
          config={formFieldDefinition}
          value={value?.value}
          previousValue={previousValue?.value}
          description={value?.description}
          previousDescription={previousValue?.description}
          onClose={closeDiffModal}
        />
      ) : null}

      <FieldHeader
        {...formFieldDefinition}
        label="AI Tools"
        isUpdated={isUpdated}
        editCallback={editCallback}
        onShowDiffModal={openDiffModal}
        availableActions={availableActions}
        entryPoint={entryPoint}
        isUpdateRequired={isUpdateRequired}
      />

      {hasValue ? (
        <AIToolsDisplay
          exists={value.exists}
          value={value.value}
          options={formFieldDefinition.params.labels.dependentOptions || {}}
          description={value.description}
        />
      ) : (
        <AIToolsEmptyState />
      )}
    </Flex>
  );
};
